/* eslint-disable no-nested-ternary */
import { Icon } from '@components';
import CertificateBackground from '@pngs/Certificate/CertificateBackground.png';
import ContentBackground from '@pngs/Certificate/ContentBackground.png';
import { Flex, theme, Typography } from '@styles';
import { ReactComponent as GenevaCallLogo } from '@svgs/geneva-call-logo.svg';
import { ReactComponent as LogoEN } from '@svgs/logo_white_en.svg';
import { ReactComponent as LogoUA } from '@svgs/logo_white_ua.svg';
import { enTranlation, uaTranlation } from '@translations';
import { bool, string } from 'prop-types';
import React, { useMemo } from 'react';

import ShadowedCard from '../ShadowedCard/ShadowedCard';

const LogoIcon = {
  en: LogoEN,
  ua: LogoUA,
};

const getTypographyStyle = (type, size) => {
  switch (type) {
    case 'title':
      return size === 'big' ? 'BN-regular-s92-lh78' : 'BN-bold-s64-lh55';
    case 'subtitle':
      return size === 'big' ? 'MS-semibold-s28-lh44' : 'MS-regular-s20-lh31';
    case 'name':
      return size === 'big' ? 'MS-bold-s57-lh60' : 'MS-bold-s40-lh42';
    case 'bold':
      return size === 'big' ? 'MS-bold-s26-lh40' : 'MS-bold-s20-lh31';
    case 'default':
      return size === 'big' ? 'MS-regular-s26-lh40' : 'MS-regular-s20-lh31';
    default:
      return 'MS-regular-s26-lh40';
  }
};

const Certificate = ({
  date,
  isPDFGenerationVariant,
  language,
  name,
  size,
  title,
  withExtraMargin,
}) => {
  const text = useMemo(
    () => (language === 'en' ? enTranlation : uaTranlation),
    [language]
  );

  const titleByLevel = {
    'Officer Level': 'OfficerLevel',
    'Soldier Level': 'SoldierLevel',
    'Рівень офіцера': 'OfficerLevel',
    'Рівень солдата': 'SoldierLevel',
  };

  return (
    <Flex
      background={`url(${CertificateBackground})`}
      border="4px solid #FD0"
      px={72}
      {...(withExtraMargin && { width: '1280px' })}
      py={75}
    >
      <Flex
        backgroundColor="#D7D7D7"
        height="100%"
        justifyContent="center"
        position="relative"
        style={{
          boxShadow: '0px 0px 28px 0px #353A33',
        }}
        width="100%"
      >
        {!isPDFGenerationVariant && (
          <Flex
            height="100%"
            left={0}
            position="absolute"
            style={{
              background: `linear-gradient(341deg, rgba(105, 105, 105, 0.00) -24.27%, #C3C3C3 83.09%), 50% / cover no-repeat url(${ContentBackground}), #FFF`,
              mixBlendMode: 'overlay',
            }}
            top={0}
            width="100%"
          />
        )}
        <ShadowedCard
          position="absolute"
          text={text.Certificate.Title}
          top="-40px"
          withBottomOffset={isPDFGenerationVariant}
        />
        <Flex
          alignItems="center"
          border="2px solid #182617"
          color={theme.colors.black[100]}
          flexDirection="column"
          height="700px"
          margin="6px"
          width="100%"
        >
          <Typography
            mt="112px"
            textAlign="center"
            variant={getTypographyStyle('subtitle', size)}
          >
            {text.Certificate.Subtitle}
          </Typography>
          <Typography
            mt="14px"
            textAlign="center"
            variant={getTypographyStyle('name', size)}
          >
            {name || text.Certificate.Name}
          </Typography>
          <Typography
            mt="47px"
            textAlign="center"
            variant={getTypographyStyle('default', size)}
          >
            {text.Certificate.For}
          </Typography>
          <Typography
            my="20px"
            textAlign="center"
            variant={getTypographyStyle('bold', size)}
          >
            {text.Certificate[titleByLevel[title]]?.toUpperCase() ||
              title.toUpperCase()}
            <br />
            {text.Certificate.OfPlatformCourse}
          </Typography>
          <Typography
            color={theme.colors.gray}
            textAlign="center"
            variant={getTypographyStyle('default', size)}
          >
            {`${text.Certificate.On} ${date}`}
          </Typography>
          <Flex mb="60px" mt="auto">
            <Icon height={97} SVG={GenevaCallLogo} width={114} />
            <Icon
              fill="#333"
              height={97}
              mx="auto"
              SVG={LogoIcon[language || 'en']}
              width={114}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

Certificate.propTypes = {
  date: string,
  isPDFGenerationVariant: bool,
  language: string,
  name: string,
  size: string,
  title: string,
  withExtraMargin: bool,
};

Certificate.defaultProps = {
  date: '**/**/****',
  isPDFGenerationVariant: false,
  language: 'en',
  name: null,
  size: 'big',
  title: '...',
  withExtraMargin: false,
};

export default Certificate;
